/**
 * ReadyMadeListingEditor — Etsy-inspired full-page listing editor for the
 * Ready-Made store, replacing the cramped 2-column modal that used to live
 * in ReadyMadeTab.jsx (iter47, Feb 2026).
 *
 * Layout
 * ──────
 *   ┌─────────────────────────────────┬──────────────────┐
 *   │ Photos (drag-reorder, up to 10) │  Live preview    │ (sticky)
 *   │ Listing details                 │                  │
 *   │ Price & inventory  (+ size var) │                  │
 *   │ Variations (colours + sizes)    │                  │
 *   │ Attributes (tags + materials)   │                  │
 *   │ Delivery (processing time)      │                  │
 *   │ SEO meta                        │                  │
 *   └─────────────────────────────────┴──────────────────┘
 *
 * The editor mounts as a fullscreen overlay (z-50) so we don't have to
 * fight the existing /admin tab router. Close → returns to the table.
 */
import { useMemo, useRef, useState } from "react";
import {
  X, Save, Upload, GripVertical, Trash2, Plus, Image as ImageIcon, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { uploadImage } from "../../lib/api";

const MAX_PHOTOS = 10;
const MAX_TAGS = 13;
const DEFAULT_COLORS = ["#000000", "#FFFFFF"];
const DEFAULT_SIZES = ["S", "M", "L", "XL", "2XL"];

// ============================================================================
// Photo grid with drag-to-reorder
// ============================================================================

function PhotoGrid({ photos, onChange }) {
  const [uploading, setUploading] = useState(false);
  const dragIdx = useRef(null);

  const addFiles = async (files) => {
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) {
      toast.error(`Max ${MAX_PHOTOS} photos`);
      return;
    }
    const toAdd = Array.from(files).slice(0, room);
    setUploading(true);
    try {
      const next = [...photos];
      for (const file of toAdd) {
        if (file.size > 8 * 1024 * 1024) {
          toast.error(`${file.name}: max 8MB`);
          continue;
        }
        const dataUrl = await new Promise((res) => {
          const r = new FileReader();
          r.onload = (e) => res(e.target.result);
          r.readAsDataURL(file);
        });
        const out = await uploadImage(dataUrl, file.name);
        next.push(out.url);
      }
      onChange(next);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDragStart = (i) => () => { dragIdx.current = i; };
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (i) => (e) => {
    e.preventDefault();
    const from = dragIdx.current;
    if (from == null || from === i) return;
    const next = [...photos];
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    onChange(next);
    dragIdx.current = null;
  };
  const removeAt = (i) => onChange(photos.filter((_, idx) => idx !== i));

  const slots = useMemo(() => {
    const arr = [...photos];
    while (arr.length < MAX_PHOTOS) arr.push(null);
    return arr;
  }, [photos]);

  return (
    <div data-testid="rm-photo-grid">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {slots.map((url, i) => {
          const filled = !!url;
          const isPrimary = i === 0 && filled;
          return (
            <div
              key={i}
              draggable={filled}
              onDragStart={onDragStart(i)}
              onDragOver={onDragOver}
              onDrop={onDrop(i)}
              className={`relative aspect-square border ${
                filled ? "border-[#333]" : "border-dashed border-[#2a2a2a]"
              } bg-[#0a0a0a] group ${filled ? "cursor-move" : ""}`}
              data-testid={`rm-photo-slot-${i}`}
            >
              {filled ? (
                <>
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  {isPrimary && (
                    <span className="absolute top-1 left-1 bg-[var(--tc-red)] text-white text-[9px] font-mono tracking-widest px-1.5 py-0.5">
                      PRIMARY
                    </span>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <GripVertical size={16} className="text-white" />
                    <button
                      onClick={() => removeAt(i)}
                      className="text-white hover:text-[var(--tc-red)]"
                      data-testid={`rm-photo-remove-${i}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-full text-zinc-600 hover:text-[var(--tc-red)] hover:border-[var(--tc-red)] cursor-pointer text-center px-2">
                  <Upload size={18} />
                  <span className="font-mono text-[9px] tracking-widest mt-2">
                    {i === 0 ? "PRIMARY" : `PHOTO ${i + 1}`}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => addFiles(e.target.files)}
                  />
                </label>
              )}
            </div>
          );
        })}
      </div>
      <div className="font-mono text-[10px] tracking-widest text-zinc-500 mt-3 flex items-center gap-2">
        <ImageIcon size={12} />
        <span>
          {photos.length}/{MAX_PHOTOS} photos · drag to reorder · first photo is the listing thumbnail
        </span>
        {uploading && <span className="text-[var(--tc-red)] animate-pulse">uploading…</span>}
      </div>
    </div>
  );
}

// ============================================================================
// Sizes input — chip-based with type+Enter / ADD button, plus quick-add
// buttons for the standard apparel sizes so the admin doesn't have to type
// them one by one. Solves the mobile-iOS comma-separated input bug where
// auto-capitalize/auto-suggest fought the split-on-every-keystroke logic
// and the user couldn't add new sizes (reported Feb 2026).
// ============================================================================

const COMMON_SIZES = ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL"];

function SizesInput({ sizes, onChange }) {
  const [draft, setDraft] = useState("");

  const add = (raw) => {
    const v = (raw ?? draft).trim().toUpperCase();
    if (!v) return;
    if (sizes.includes(v)) {
      toast.error(`${v} already added`);
      return;
    }
    if (sizes.length >= 20) {
      toast.error("Max 20 sizes");
      return;
    }
    onChange([...sizes, v]);
    setDraft("");
  };

  return (
    <div data-testid="rm-sizes-input">
      <div className="flex flex-wrap gap-2 mb-3 min-h-[28px]">
        {sizes.map((s) => (
          <span
            key={s}
            className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#161616] border border-[#2a2a2a] font-mono text-[10px] tracking-widest text-zinc-200"
          >
            {s}
            <button
              type="button"
              onClick={() => onChange(sizes.filter((x) => x !== s))}
              className="text-zinc-500 hover:text-[var(--tc-red)]"
              data-testid={`rm-size-remove-${s}`}
              aria-label={`Remove ${s}`}
            >
              <X size={11} />
            </button>
          </span>
        ))}
        {sizes.length === 0 && (
          <span className="font-mono text-[10px] tracking-widest text-zinc-600 self-center">
            no sizes added yet
          </span>
        )}
      </div>

      <div className="flex gap-2 mb-3">
        <input
          value={draft}
          // iOS autoCapitalize="characters" so the keyboard offers uppercase
          // by default — exactly what apparel sizes need (S, M, XL…).
          // autoCorrect=off prevents "S" → "Is" auto-correction nonsense.
          autoCapitalize="characters"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add();
            }
          }}
          placeholder="e.g. XL or 3XL"
          className="flex-1 bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-mono text-sm uppercase outline-none"
          data-testid="rm-size-draft"
        />
        <button
          type="button"
          onClick={() => add()}
          className="px-4 py-2 border border-[#333] hover:border-[var(--tc-red)] font-mono text-[10px] tracking-widest"
          data-testid="rm-size-add"
        >
          ADD
        </button>
      </div>

      <div className="font-mono text-[9px] tracking-widest text-zinc-500 mb-2">QUICK ADD</div>
      <div className="flex flex-wrap gap-1.5">
        {COMMON_SIZES.map((s) => {
          const already = sizes.includes(s);
          return (
            <button
              key={s}
              type="button"
              onClick={() => !already && add(s)}
              disabled={already}
              className={`px-2.5 py-1 font-mono text-[10px] tracking-widest border ${
                already
                  ? "border-[#1a1a1a] text-zinc-700 cursor-not-allowed"
                  : "border-[#222] text-zinc-400 hover:border-[var(--tc-red)] hover:text-white"
              }`}
              data-testid={`rm-size-quick-${s}`}
            >
              + {s}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Tag chips input (max 13)
// ============================================================================

function TagsInput({ tags, onChange }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim().toLowerCase();
    if (!v) return;
    if (tags.includes(v)) {
      toast.error("Tag already added");
      return;
    }
    if (tags.length >= MAX_TAGS) {
      toast.error(`Max ${MAX_TAGS} tags`);
      return;
    }
    onChange([...tags, v]);
    setDraft("");
  };
  return (
    <div data-testid="rm-tags-input">
      <div className="flex flex-wrap gap-2 mb-2 min-h-[28px]">
        {tags.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 px-2 py-1 bg-[#161616] border border-[#2a2a2a] font-mono text-[10px] tracking-widest text-zinc-300"
          >
            {t.toUpperCase()}
            <button
              onClick={() => onChange(tags.filter((x) => x !== t))}
              className="text-zinc-500 hover:text-[var(--tc-red)]"
              data-testid={`rm-tag-remove-${t}`}
            >
              <X size={11} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
          placeholder="e.g. toronto, raptors, vintage"
          className="flex-1 bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-body text-sm outline-none"
          data-testid="rm-tag-draft"
        />
        <button
          onClick={add}
          disabled={tags.length >= MAX_TAGS}
          className="px-3 py-2 border border-[#333] hover:border-[var(--tc-red)] font-mono text-[10px] tracking-widest disabled:opacity-30"
          data-testid="rm-tag-add"
        >
          ADD
        </button>
      </div>
      <div className="font-mono text-[10px] tracking-widest text-zinc-500 mt-2">
        {tags.length}/{MAX_TAGS} tags · helps buyers find this design via search
      </div>
    </div>
  );
}

// ============================================================================
// Colour chips (hex) — one-tap apparel palette + custom-hex fallback
// ============================================================================

// Standard apparel-blank colours that account for ~95% of t-shirt orders.
// Pre-sorted by category so the admin can glance and tap rather than reach
// for a hex picker. Add/edit safely — purely visual, no schema impact.
const APPAREL_PALETTE = [
  { hex: "#000000", name: "Black" },
  { hex: "#FFFFFF", name: "White" },
  { hex: "#1F1F1F", name: "Off Black" },
  { hex: "#4A4A4A", name: "Charcoal" },
  { hex: "#A8A8A8", name: "Heather Grey" },
  { hex: "#E5E2DA", name: "Natural" },
  { hex: "#0D1F3C", name: "Navy" },
  { hex: "#2E5BBA", name: "Royal Blue" },
  { hex: "#7BB7E0", name: "Sky Blue" },
  { hex: "#1E5631", name: "Forest" },
  { hex: "#6B8E23", name: "Olive" },
  { hex: "#C8B273", name: "Sand" },
  { hex: "#8B0000", name: "Maroon" },
  { hex: "#D32F2F", name: "Red" },
  { hex: "#E91E63", name: "Pink" },
  { hex: "#F57C00", name: "Burnt Orange" },
  { hex: "#FBC02D", name: "Mustard" },
  { hex: "#6A1B9A", name: "Purple" },
];

function ColorChips({ colors, onChange }) {
  // Custom-hex fallback for the rare design that needs an exact swatch.
  // Default to a valid 6-digit hex so the native <input type="color"> doesn't
  // emit a "non-#rrggbb value" console warning before the user types anything.
  const [draft, setDraft] = useState("#000000");

  const addHex = (raw) => {
    const v = (raw ?? draft).trim().toUpperCase();
    if (!/^#[0-9A-F]{6}$/.test(v)) {
      toast.error("Hex must look like #FF0000");
      return;
    }
    if (colors.includes(v)) {
      toast.error("Colour already added");
      return;
    }
    onChange([...colors, v]);
    setDraft("#000000");
  };

  return (
    <div data-testid="rm-colors-input">
      {/* Active chips */}
      <div className="flex flex-wrap gap-2 mb-3 min-h-[28px]">
        {colors.map((c) => {
          const named = APPAREL_PALETTE.find((p) => p.hex.toUpperCase() === c.toUpperCase());
          return (
            <span
              key={c}
              className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#161616] border border-[#2a2a2a] font-mono text-[10px] tracking-widest text-zinc-200"
            >
              <span className="w-3.5 h-3.5 border border-white/30" style={{ background: c }} />
              {named ? named.name.toUpperCase() : c}
              <button
                type="button"
                onClick={() => onChange(colors.filter((x) => x !== c))}
                className="text-zinc-500 hover:text-[var(--tc-red)] ml-0.5"
                aria-label={`Remove ${c}`}
                data-testid={`rm-color-remove-${c.replace("#", "")}`}
              >
                <X size={11} />
              </button>
            </span>
          );
        })}
        {colors.length === 0 && (
          <span className="font-mono text-[10px] tracking-widest text-zinc-600 self-center">
            no colours added yet
          </span>
        )}
      </div>

      {/* One-tap apparel palette */}
      <div className="font-mono text-[9px] tracking-widest text-zinc-500 mb-2">
        APPAREL PALETTE
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4" data-testid="rm-color-palette">
        {APPAREL_PALETTE.map((p) => {
          const already = colors.includes(p.hex);
          return (
            <button
              key={p.hex}
              type="button"
              onClick={() => !already && onChange([...colors, p.hex])}
              disabled={already}
              className={`group flex items-center gap-2 p-2 border transition-colors ${
                already
                  ? "border-[#1a1a1a] opacity-40 cursor-not-allowed"
                  : "border-[#222] hover:border-[var(--tc-red)]"
              }`}
              data-testid={`rm-color-swatch-${p.hex.replace("#", "")}`}
              title={p.hex}
            >
              <span
                className="w-6 h-6 border border-white/20 shrink-0"
                style={{ background: p.hex }}
              />
              <span className="font-mono text-[9px] tracking-widest text-zinc-400 truncate">
                {p.name.toUpperCase()}
              </span>
            </button>
          );
        })}
      </div>

      {/* Custom-hex fallback */}
      <div className="font-mono text-[9px] tracking-widest text-zinc-500 mb-2">
        CUSTOM HEX
      </div>
      <div className="flex gap-2">
        <input
          type="color"
          value={draft}
          onChange={(e) => setDraft(e.target.value.toUpperCase())}
          className="w-12 h-9 bg-black border border-[#222] cursor-pointer"
          aria-label="Custom hex picker"
          data-testid="rm-color-picker"
        />
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addHex(); } }}
          placeholder="#FF0000"
          className="flex-1 bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-mono text-xs uppercase outline-none"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          data-testid="rm-color-draft"
        />
        <button
          type="button"
          onClick={() => addHex()}
          className="px-4 py-2 border border-[#333] hover:border-[var(--tc-red)] font-mono text-[10px] tracking-widest"
          data-testid="rm-color-add"
        >
          ADD
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Per-size variations table
// ============================================================================

function SizeVariations({ sizes, variations, basePrice, defaultStock, onChange }) {
  // Each row: { size, price_delta, stock }. We hydrate from `sizes` so the
  // admin sees a row for every size they offer, even if the override is 0.
  const rows = useMemo(() => {
    return sizes.map((s) => {
      const existing = (variations || []).find((v) => v.size === s);
      return existing || { size: s, price_delta: 0, stock: defaultStock };
    });
  }, [sizes, variations, defaultStock]);

  const update = (size, patch) => {
    const next = rows.map((r) => (r.size === size ? { ...r, ...patch } : r));
    // Only persist rows with a non-default override so the variations
    // array stays small. Empty array → fall back to single price/stock.
    const dirty = next.filter(
      (r) => Number(r.price_delta) !== 0 || Number(r.stock) !== Number(defaultStock)
    );
    onChange(dirty);
  };

  if (!sizes.length) {
    return (
      <p className="font-mono text-[11px] tracking-widest text-zinc-500">
        Add at least one size above to set per-size variations.
      </p>
    );
  }

  return (
    <div className="border border-[#222]" data-testid="rm-size-variations">
      <div className="grid grid-cols-4 gap-2 px-3 py-2 bg-[#0a0a0a] border-b border-[#222] font-mono text-[9px] tracking-widest text-zinc-500">
        <div>SIZE</div>
        <div>PRICE</div>
        <div>± DELTA (CAD)</div>
        <div>STOCK</div>
      </div>
      {rows.map((r) => {
        const totalPrice = (Number(basePrice) || 0) + (Number(r.price_delta) || 0);
        return (
          <div
            key={r.size}
            className="grid grid-cols-4 gap-2 items-center px-3 py-2 border-b border-[#1a1a1a] last:border-b-0"
            data-testid={`rm-size-row-${r.size}`}
          >
            <div className="font-mono text-xs text-zinc-200">{r.size}</div>
            <div className="font-mono text-xs text-[var(--tc-red)]">${totalPrice.toFixed(2)}</div>
            <input
              type="number"
              step="0.50"
              value={r.price_delta}
              onChange={(e) => update(r.size, { price_delta: parseFloat(e.target.value) || 0 })}
              className="bg-black border border-[#222] px-2 py-1 font-mono text-xs outline-none focus:border-[var(--tc-red)]"
            />
            <input
              type="number"
              value={r.stock}
              onChange={(e) => update(r.size, { stock: parseInt(e.target.value) || 0 })}
              className="bg-black border border-[#222] px-2 py-1 font-mono text-xs outline-none focus:border-[var(--tc-red)]"
            />
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Live preview card (mirrors the public ReadyMadeCard shape)
// ============================================================================

function LivePreview({ form, startingPrice }) {
  const photos = form.gallery.length ? form.gallery : (form.image_front ? [form.image_front] : []);
  const primary = photos[0];
  const badges = form.badges || [];
  return (
    <div className="surface-card overflow-hidden" data-testid="rm-live-preview">
      <div className="px-4 py-3 border-b border-[#222] flex items-center gap-2">
        <Eye size={14} className="text-[var(--tc-red)]" />
        <span className="font-mono text-[10px] tracking-widest text-zinc-500">LIVE PREVIEW</span>
      </div>
      <div className="aspect-[4/5] bg-[#080808] relative overflow-hidden">
        {primary ? (
          <img src={primary} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-700">
            <ImageIcon size={48} />
          </div>
        )}
        <div className="absolute top-3 left-3 flex flex-col gap-1 items-start">
          {badges.includes("trending") && <span className="badge-trending">TRENDING</span>}
          {badges.includes("new-drop") && <span className="badge-newdrop">NEW DROP</span>}
          {badges.includes("bestseller") && <span className="badge-bestseller">BESTSELLER</span>}
        </div>
        <div className="absolute bottom-3 right-3 flex gap-1">
          {(form.colors || []).slice(0, 4).map((c) => (
            <span key={c} className="w-3.5 h-3.5 border border-white/40" style={{ background: c }} />
          ))}
        </div>
      </div>
      <div className="p-4">
        <div className="font-ui font-bold uppercase tracking-wider text-xs truncate">
          {form.name || "Your design name"}
        </div>
        <div className="font-mono text-[10px] tracking-widest text-zinc-500 mt-1 capitalize">
          {(form.category || "category").replace("-", " ")}
        </div>
        <div className="flex items-baseline justify-between mt-3">
          <div className="font-mono font-bold text-[var(--tc-red)] text-sm">
            From&nbsp;${Number(startingPrice || form.price || 0).toFixed(2)}
          </div>
          {form.processing_time && (
            <div className="font-mono text-[9px] tracking-widest text-zinc-500">
              SHIPS · {form.processing_time.toUpperCase()}
            </div>
          )}
        </div>
        {form.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-[#1a1a1a]">
            {form.tags.slice(0, 5).map((t) => (
              <span
                key={t}
                className="font-mono text-[9px] tracking-widest text-zinc-500"
              >
                #{t}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Reusable section card
// ============================================================================

function Section({ title, subtitle, required, children, testId }) {
  return (
    <section className="surface-card p-6" data-testid={testId}>
      <div className="mb-5">
        <h3 className="font-display text-xl uppercase tracking-wider">
          {title}
          {required && <span className="text-[var(--tc-red)] ml-1">*</span>}
        </h3>
        {subtitle && (
          <p className="font-body text-xs text-zinc-500 mt-1.5">{subtitle}</p>
        )}
      </div>
      {children}
    </section>
  );
}

function Label({ children, hint }) {
  return (
    <label className="block mb-3">
      <span className="font-mono text-[10px] tracking-widest text-zinc-500">
        {String(children).toUpperCase()}
      </span>
      {hint && (
        <span className="block font-body text-[11px] text-zinc-500 normal-case mt-0.5 tracking-normal">
          {hint}
        </span>
      )}
    </label>
  );
}

// ============================================================================
// MAIN EDITOR
// ============================================================================

export default function ReadyMadeListingEditor({ item, onClose, onSave }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => {
    const startingGallery = item?.gallery?.length
      ? item.gallery
      : [item?.image_front, item?.image_back].filter(Boolean);
    return {
      name: item?.name || "",
      category: item?.category || "anime",
      price: item?.price ?? 32.99,
      description: item?.description || "",
      gallery: startingGallery,
      active: item?.active ?? true,
      stock: item?.stock ?? 100,
      cost_per_unit: item?.cost_per_unit ?? 0,
      colors: item?.colors || DEFAULT_COLORS,
      sizes: item?.sizes || DEFAULT_SIZES,
      garment_types: item?.garment_types?.length ? item.garment_types : ["tshirt", "sweatshirt", "hoodie"],
      garment_prices: item?.garment_prices && Object.keys(item.garment_prices).length
        ? item.garment_prices
        : { tshirt: 35, sweatshirt: 45, hoodie: 60 },
      badges: item?.badges || [],
      tags: item?.tags || [],
      materials: item?.materials || "",
      production_partner: item?.production_partner || "",
      processing_time: item?.processing_time || "1-2 business days",
      size_variations: item?.size_variations || [],
      seo_title: item?.seo_title || "",
      seo_description: item?.seo_description || "",
      // iter47w — Google Merchant TSV feed overrides
      gmc_enabled: item?.gmc_enabled !== false,
      google_local_availability: item?.google_local_availability || "",
      pickup_sla: item?.pickup_sla || "",
      gtin: item?.gtin || "",
      mpn: item?.mpn || "",
    };
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setGarmentPrice = (gid, val) =>
    setForm((f) => ({ ...f, garment_prices: { ...f.garment_prices, [gid]: parseFloat(val) || 0 } }));
  const toggleBadge = (b) =>
    setForm((f) => ({
      ...f,
      badges: f.badges.includes(b) ? f.badges.filter((x) => x !== b) : [...f.badges, b],
    }));

  // Derived "starting from" price = min of enabled garment prices. Used in
  // the live preview, the listing grid, search, and JSON-LD structured data.
  const startingPrice = useMemo(() => {
    const enabled = (form.garment_types || []).filter((g) => form.garment_prices?.[g] != null);
    if (!enabled.length) return 0;
    return Math.min(...enabled.map((g) => Number(form.garment_prices[g]) || 0));
  }, [form.garment_types, form.garment_prices]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Add a listing title");
      return;
    }
    if (!form.gallery.length) {
      toast.error("Upload at least one photo");
      return;
    }
    setSaving(true);
    try {
      // Backend still expects image_front / image_back. Map first/last
      // gallery photo into those so older PDP code keeps working.
      // `price` is derived from min(enabled garment_prices) so legacy
      // surfaces (grid, search, JSON-LD) keep working with no migration.
      const payload = {
        ...form,
        price: startingPrice,
        stock: parseInt(form.stock) || 0,
        cost_per_unit: parseFloat(form.cost_per_unit) || 0,
        image_front: form.gallery[0] || "",
        image_back: form.gallery[1] || "",
      };
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black overflow-y-auto" data-testid="rm-editor">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-black/95 backdrop-blur border-b border-[#222]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="text-zinc-400 hover:text-white"
              data-testid="rm-editor-close"
            >
              <X size={20} />
            </button>
            <div>
              <h2 className="font-display text-2xl uppercase tracking-wider">
                {item ? "Edit Listing" : "New Listing"}
              </h2>
              <p className="font-mono text-[10px] tracking-widest text-zinc-500 mt-0.5">
                READY-MADE DESIGN · TORONTO CUSTOM T-SHIRTS
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-zinc-400">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => set("active", e.target.checked)}
                data-testid="rm-active-toggle"
              />
              ACTIVE
            </label>
            <button
              onClick={onClose}
              className="btn-tc-secondary px-4 py-2 text-xs"
              data-testid="rm-editor-cancel"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-tc-primary flex items-center gap-2 px-5 py-2 text-xs disabled:opacity-40"
              data-testid="rm-editor-save"
            >
              <Save size={14} /> {saving ? "SAVING…" : "PUBLISH"}
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-6 py-8 grid lg:grid-cols-[1fr_360px] gap-8">
        {/* LEFT — sections */}
        <div className="space-y-6">
          {/* PHOTOS */}
          <Section
            title="Photos"
            subtitle={`Add up to ${MAX_PHOTOS} photos. The first photo becomes the listing thumbnail — drag any tile to reorder.`}
            required
            testId="rm-section-photos"
          >
            <PhotoGrid photos={form.gallery} onChange={(g) => set("gallery", g)} />
          </Section>

          {/* LISTING DETAILS */}
          <Section
            title="Listing details"
            subtitle="What you're selling — clear titles and descriptions help buyers (and Google) find this design."
            required
            testId="rm-section-details"
          >
            <Label>Title</Label>
            <input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              maxLength={140}
              placeholder="Vintage Toronto Skyline — Black Tee"
              className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-body text-sm outline-none mb-4"
              data-testid="rm-field-name"
            />

            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <Label>Category</Label>
                <select
                  value={form.category}
                  onChange={(e) => set("category", e.target.value)}
                  className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-body text-sm outline-none"
                  data-testid="rm-field-category"
                >
                  {["anime", "gaming", "music", "sports", "toronto", "vintage", "minimal", "y2k", "streetwear", "other"].map((c) => (
                    <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Badges</Label>
                <div className="flex flex-wrap gap-2 pt-1">
                  {["trending", "new-drop", "bestseller"].map((b) => (
                    <button
                      key={b}
                      type="button"
                      onClick={() => toggleBadge(b)}
                      className={`px-3 py-1.5 font-mono text-[10px] tracking-widest border ${
                        form.badges.includes(b)
                          ? "border-[var(--tc-red)] bg-[var(--tc-red)] text-white"
                          : "border-[#222] text-zinc-400 hover:border-[#444]"
                      }`}
                      data-testid={`rm-badge-${b}`}
                    >
                      {b.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <Label hint="3–5 sentences. Mention the print method, fit and inspiration.">
              Description
            </Label>
            <textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              rows={5}
              maxLength={1500}
              placeholder="Heavyweight 100% ringspun cotton. DTF-printed in Toronto…"
              className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-body text-sm outline-none resize-none"
              data-testid="rm-field-description"
            />
            <div className="text-right font-mono text-[10px] text-zinc-600 mt-1">
              {form.description.length}/1500
            </div>
          </Section>

          {/* PRICE & INVENTORY — driven BY garment type (iter47d) */}
          <Section
            title="Price &amp; inventory"
            subtitle="Each garment type has its own price. The buyer picks one on the product page; per-size deltas apply on top."
            required
            testId="rm-section-price"
          >
            <Label hint="Toggle a card to enable/disable that garment. Edit the price field on each enabled card.">
              Garment types &amp; pricing
            </Label>
            <div className="grid sm:grid-cols-3 gap-3 mb-6" data-testid="rm-garment-picker">
              {[
                { id: "tshirt", label: "T-Shirt", blurb: "100% Premium Cotton · DTF Print" },
                { id: "sweatshirt", label: "Sweatshirt", blurb: "Heavyweight Crewneck · DTF Print" },
                { id: "hoodie", label: "Hoodie", blurb: "Heavyweight Pullover · DTF Print" },
              ].map((g) => {
                const enabled = form.garment_types.includes(g.id);
                const priceVal = form.garment_prices?.[g.id] ?? "";
                return (
                  <div
                    key={g.id}
                    className={`p-4 border transition-colors ${
                      enabled
                        ? "border-[var(--tc-red)] bg-[var(--tc-red)]/5"
                        : "border-[#222] opacity-60"
                    }`}
                    data-testid={`rm-garment-card-${g.id}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-ui font-bold uppercase tracking-wider text-sm">{g.label}</span>
                      <button
                        type="button"
                        onClick={() => {
                          const next = enabled
                            ? form.garment_types.filter((x) => x !== g.id)
                            : [...form.garment_types, g.id];
                          if (next.length === 0) {
                            toast.error("Pick at least one garment type");
                            return;
                          }
                          set("garment_types", next);
                        }}
                        className={`w-5 h-5 border flex items-center justify-center text-[11px] ${
                          enabled
                            ? "border-[var(--tc-red)] bg-[var(--tc-red)] text-white"
                            : "border-[#444] text-zinc-600 hover:border-zinc-300"
                        }`}
                        data-testid={`rm-garment-${g.id}`}
                        aria-label={enabled ? `Disable ${g.label}` : `Enable ${g.label}`}
                      >
                        {enabled ? "✓" : ""}
                      </button>
                    </div>
                    <div className="flex items-center">
                      <span className="px-2 py-2 bg-[#0a0a0a] border border-[#222] border-r-0 font-mono text-xs text-zinc-500">CA$</span>
                      <input
                        type="number"
                        step="0.50"
                        value={priceVal}
                        disabled={!enabled}
                        onChange={(e) => setGarmentPrice(g.id, e.target.value)}
                        className="flex-1 min-w-0 bg-black border border-[#222] focus:border-[var(--tc-red)] px-2 py-2 font-mono text-base text-[var(--tc-red)] outline-none disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`rm-garment-price-${g.id}`}
                      />
                    </div>
                    <div className="font-mono text-[9px] text-zinc-500 mt-2 leading-tight">{g.blurb}</div>
                  </div>
                );
              })}
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div>
                <Label>Default stock</Label>
                <input
                  type="number"
                  value={form.stock}
                  onChange={(e) => set("stock", e.target.value)}
                  className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-mono text-sm outline-none"
                  data-testid="rm-field-stock"
                />
              </div>
              <div>
                <Label hint="Internal only — buyer never sees this">Cost / unit (CAD)</Label>
                <div className="flex">
                  <span className="px-3 py-2 bg-[#0a0a0a] border border-[#222] border-r-0 font-mono text-xs text-zinc-500">CA$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={form.cost_per_unit}
                    onChange={(e) => set("cost_per_unit", e.target.value)}
                    className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-mono text-sm outline-none"
                    data-testid="rm-field-cost"
                  />
                </div>
              </div>
            </div>

            <Label hint="Charge a non-zero delta to bump bigger sizes (e.g. +$5 on 3XL). The delta is added to whichever garment the buyer picks. Empty rows fall back to the garment price + default stock.">
              Per-size variations
            </Label>
            <SizeVariations
              sizes={form.sizes}
              variations={form.size_variations}
              basePrice={startingPrice}
              defaultStock={form.stock}
              onChange={(v) => set("size_variations", v)}
            />
          </Section>

          {/* VARIATIONS */}
          <Section
            title="Variations"
            subtitle="Which colours and sizes you offer for this listing."
            testId="rm-section-variations"
          >
            <Label>Colours</Label>
            <ColorChips
              colors={form.colors}
              onChange={(c) => set("colors", c)}
            />

            <div className="mt-5">
              <Label hint="Type a size and press Enter (or tap ADD). Use the quick-add buttons for the standard apparel sizes.">
                Sizes
              </Label>
              <SizesInput sizes={form.sizes} onChange={(s) => set("sizes", s)} />
            </div>
          </Section>

          {/* ATTRIBUTES */}
          <Section
            title="Attributes"
            subtitle="Helps buyers find this listing via on-site search and SEO."
            testId="rm-section-attributes"
          >
            <Label hint={`Press Enter or comma to add. Up to ${MAX_TAGS} tags.`}>Tags</Label>
            <TagsInput tags={form.tags} onChange={(t) => set("tags", t)} />

            <div className="grid sm:grid-cols-2 gap-4 mt-5">
              <div>
                <Label hint="Shown publicly under 'Materials' on the product page">Materials</Label>
                <input
                  value={form.materials}
                  onChange={(e) => set("materials", e.target.value)}
                  placeholder="100% ringspun cotton, durable full-colour DTF print"
                  className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-body text-sm outline-none"
                  data-testid="rm-field-materials"
                />
              </div>
              <div>
                <Label hint="Internal — who prints this for you">Production partner</Label>
                <input
                  value={form.production_partner}
                  onChange={(e) => set("production_partner", e.target.value)}
                  placeholder="In-house · Toronto"
                  className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-body text-sm outline-none"
                  data-testid="rm-field-partner"
                />
              </div>
            </div>
          </Section>

          {/* DELIVERY */}
          <Section
            title="Delivery & processing"
            subtitle="Give buyers a clear, honest fulfillment estimate."
            testId="rm-section-delivery"
          >
            <Label>Processing time</Label>
            <select
              value={form.processing_time}
              onChange={(e) => set("processing_time", e.target.value)}
              className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-body text-sm outline-none"
              data-testid="rm-field-processing"
            >
              <option>1-2 business days</option>
              <option>2-3 business days</option>
              <option>3-5 business days</option>
              <option>5-7 business days</option>
              <option>Made-to-order · 7-10 days</option>
            </select>
          </Section>

          {/* SEO */}
          <Section
            title="SEO &amp; search"
            subtitle="Optional. Overrides the auto-generated meta tags for this product page."
            testId="rm-section-seo"
          >
            <Label hint="Tip: lead with the product, follow with the city + key keywords. Max 60 chars renders in Google.">
              SEO title
            </Label>
            <input
              value={form.seo_title}
              onChange={(e) => set("seo_title", e.target.value)}
              maxLength={70}
              placeholder="Vintage Toronto Skyline Tee — Streetwear · Toronto Custom T-Shirts"
              className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-body text-sm outline-none mb-1"
              data-testid="rm-field-seo-title"
            />
            <div className="text-right font-mono text-[10px] text-zinc-600 mb-4">
              {form.seo_title.length}/70
            </div>

            <Label hint="Max 160 chars — that's what shows under the link in Google search results.">
              SEO description
            </Label>
            <textarea
              value={form.seo_description}
              onChange={(e) => set("seo_description", e.target.value)}
              rows={3}
              maxLength={170}
              placeholder="Heavyweight cotton tee with a hand-drawn Toronto skyline — printed in the 6ix, ships in 1-2 days."
              className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-body text-sm outline-none resize-none"
              data-testid="rm-field-seo-desc"
            />
            <div className="text-right font-mono text-[10px] text-zinc-600 mt-1">
              {form.seo_description.length}/170
            </div>
          </Section>

          {/* Google Merchant — TSV feeds (iter47w) */}
          <Section
            title="Google Merchant Feeds"
            subtitle="Per-product overrides for the Free Listings + Local Inventory TSV feeds. Leave blank to use the defaults from Admin → Google Local Feeds."
            testId="rm-section-gmc"
          >
            <label className="flex items-center gap-2 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={form.gmc_enabled}
                onChange={(e) => set("gmc_enabled", e.target.checked)}
                data-testid="rm-field-gmc-enabled"
                className="accent-[var(--tc-red)]"
              />
              <span className="font-mono text-xs tracking-widest text-zinc-300">
                INCLUDE IN GOOGLE FEEDS
              </span>
            </label>

            <Label hint="Override automatic stock-based availability for the Toronto store. 'exclude' drops this product from the Local Inventory feed entirely.">
              Local availability override
            </Label>
            <select
              value={form.google_local_availability}
              onChange={(e) => set("google_local_availability", e.target.value)}
              data-testid="rm-field-google-local-availability"
              className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-body text-sm outline-none mb-4"
            >
              <option value="">Auto (derive from stock)</option>
              <option value="in_stock">In stock</option>
              <option value="limited_availability">Limited availability</option>
              <option value="out_of_stock">Out of stock</option>
              <option value="exclude">Exclude from local feed</option>
            </select>

            <Label hint="How fast a customer can pick up at the Toronto store. Leave blank to use the default pickup SLA.">
              Pickup SLA override
            </Label>
            <select
              value={form.pickup_sla}
              onChange={(e) => set("pickup_sla", e.target.value)}
              data-testid="rm-field-pickup-sla"
              className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-body text-sm outline-none mb-4"
            >
              <option value="">Use default</option>
              <option value="same_day">same_day</option>
              <option value="next_day">next_day</option>
              <option value="2-day">2-day</option>
              <option value="3-day">3-day</option>
              <option value="4-day">4-day</option>
              <option value="5-day">5-day</option>
              <option value="6-day">6-day</option>
              <option value="7-day">7-day</option>
              <option value="multi-week">multi-week</option>
            </select>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label hint="Optional — UPC/EAN/ISBN. Leave blank if no barcode.">GTIN</Label>
                <input
                  value={form.gtin}
                  onChange={(e) => set("gtin", e.target.value)}
                  placeholder=""
                  data-testid="rm-field-gtin"
                  className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-body text-sm outline-none"
                />
              </div>
              <div>
                <Label hint="Optional — Manufacturer Part Number.">MPN</Label>
                <input
                  value={form.mpn}
                  onChange={(e) => set("mpn", e.target.value)}
                  placeholder=""
                  data-testid="rm-field-mpn"
                  className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-body text-sm outline-none"
                />
              </div>
            </div>
          </Section>
        </div>

        {/* RIGHT — sticky live preview */}
        <aside className="lg:sticky lg:top-24 lg:self-start space-y-4">
          <LivePreview form={form} startingPrice={startingPrice} />
          <div className="surface-card p-4">
            <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-3">PUBLISH CHECKLIST</div>
            <ul className="space-y-2 text-xs font-body">
              <ChecklistItem ok={!!form.name.trim()} label="Title set" />
              <ChecklistItem ok={form.gallery.length >= 1} label="At least 1 photo" />
              <ChecklistItem ok={form.gallery.length >= 4} label="4+ photos (recommended)" />
              <ChecklistItem ok={form.description.length >= 60} label="Description ≥ 60 chars" />
              <ChecklistItem ok={form.tags.length >= 5} label="5+ tags (boosts search)" />
              <ChecklistItem ok={form.colors.length >= 1} label="Colour set" />
              <ChecklistItem ok={form.sizes.length >= 1} label="Sizes set" />
              <ChecklistItem ok={!!form.materials.trim()} label="Materials filled" />
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}

function ChecklistItem({ ok, label }) {
  return (
    <li className="flex items-center gap-2 text-zinc-300">
      <span
        className={`w-4 h-4 flex items-center justify-center border ${
          ok ? "border-green-500 text-green-500" : "border-[#333] text-zinc-700"
        }`}
      >
        {ok ? "✓" : <Plus size={10} />}
      </span>
      {label}
    </li>
  );
}
